import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { EContractStatus, EProcessNames } from "api/enums";
import { EContractsSortField } from "api/enums/EContractsSortField";
import clsx from "clsx";
import ContractFilters from "components/Dashboard/ContractFilters";
import ContractsDashboard from "components/Dashboard/ContractsDashboard";
import Profile from "components/Profile/Profile";
import TabPanel from "components/UI/Tabs/TabPanel";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import ContractForm from "../components/Contract/ContractForm";
import ContractsKanban from "../components/Kanban/ContractsKanban";
import { BackButton } from "../components/UI/Buttons";
import SearchField from "../components/UI/Fields/SearchField";
import { Page, PageHeader, PageTitle } from "../components/UI/Page";
import { SystemMessageSnack } from "../components/UI/System";
import TopBar from "../components/shared/TopBar";
import { lang } from "../i18nLang";
import {
  AccessProfileStore,
  ContractAttachmentStore,
  ContractListStore,
  ContractStore,
  KanbanStore,
  MessageStore,
  ProfileStore,
} from "../store";

const useStyles = makeStyles((theme) => ({
  search: { margin: theme.spacing(2, 0) },
  sortInput: { width: 180, margin: theme.spacing(2, 0) },
  filterInput: { width: 180, margin: theme.spacing(2, 4) },
  filterLabel: { width: "100%" },
  addButton: { margin: theme.spacing(2, 0, 2, 2) },
  formPage: { backgroundColor: "#E5E5E5" },
  tabsContainer: { minHeight: 63 },
}));

const Kanban = observer(() => {
  const classes = useStyles();
  const { enableLayerProfile } = ProfileStore;
  const { sortField, filterStatus } = KanbanStore;
  const contractCount = KanbanStore.filteredContracts.length;
  const [searchValue, setSearchValue] = useState(KanbanStore.filterSearch);
  const [currentTab, setCurrentTab] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const sortFieldValues = Object.values(EContractsSortField);
  const filterStatusValues = Object.values(EContractStatus);

  useEffect(() => {
    KanbanStore.setStatusFilterWithUserConfig();
    ContractListStore.subscribeContractListObserver();
    return () => {
      ContractListStore.unscribeContracts();
    };
  }, []);

  const handleClickNew = () => {
    if (
      !AccessProfileStore.validateAccessPermissionTo(
        EProcessNames.GERAR_CONTRATO
      )
    ) {
      MessageStore.error(lang("userProfile.profileDoesNotAllow"));
      return;
    }
    ContractStore.generateAndSetCreatedContractId();
    setCurrentTab(2);
    setOpenForm(true);
  };

  const handleClickClose = async () => {
    setCurrentTab(0);
    setOpenForm(false);
    await ContractAttachmentStore.removeAllUnsavedContractAttachment();
    ContractAttachmentStore.reset();
    ContractStore.clearCreatedContractId();
  };

  const handleChangeSearch = (ev) => {
    const value = ev.target.value;
    setSearchValue(value);
    KanbanStore.filterSearch = value;
    KanbanStore.refreshFilteredContracts();
  };

  const handleChangeTab = (event, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleChangeSortInput = (ev) => {
    const { value } = ev.target;
    KanbanStore.setSortField(value);
    KanbanStore.refreshFilteredContracts();
  };

  const handleCheckFilterOption = (ev, checked: boolean) => {
    const { name } = ev.target;
    KanbanStore.setAndSaveFilterStatus(name, checked).catch((error) =>
      MessageStore.error(lang("kanban.saveFilterStatusError", error?.message))
    );
    KanbanStore.refreshFilteredContracts();
  };

  return (
    <>
      <TopBar enableActions={true} />
      {!enableLayerProfile && (
        <>
          <Page className={clsx({ [classes.formPage]: currentTab === 2 })}>
            {currentTab !== 2 && (
              <PageHeader container spacing={1} padding={[0, 1.5]}>
                <Grid item xs={12} sm={4} md={4}>
                  <Tabs
                    value={currentTab}
                    indicatorColor="primary"
                    textColor="primary"
                    className={classes.tabsContainer}
                    onChange={handleChangeTab}
                  >
                    <Tab label={lang("kanban.tab1", contractCount)} />
                    <Tab label={lang("kanban.tab2")} />
                  </Tabs>
                </Grid>
                {currentTab === 0 && (
                  <Grid container item xs={12} sm md justifyContent="flex-end">
                    <TextField
                      select
                      className={classes.sortInput}
                      value={sortField}
                      variant="outlined"
                      size="small"
                      onChange={handleChangeSortInput}
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value={""} disabled>
                        {lang("kanban.ordering")}
                      </MenuItem>
                      {sortFieldValues.map((value) => (
                        <MenuItem key={value} value={value}>
                          {lang(`kanban.sortFields.${value}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      className={classes.filterInput}
                      value={""}
                      variant="outlined"
                      size="small"
                      SelectProps={{ displayEmpty: true }}
                    >
                      <MenuItem value={""} disabled>
                        {lang("kanban.filterByStatus")}
                      </MenuItem>
                      {filterStatusValues.map((stt) => (
                        <MenuItem key={stt} value={stt}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name={stt}
                                color="primary"
                                checked={filterStatus[stt]}
                                onChange={handleCheckFilterOption}
                              />
                            }
                            className={classes.filterLabel}
                            label={lang(`kanban.status.${stt}`)}
                            onClick={(ev) => ev.stopPropagation()}
                          />
                        </MenuItem>
                      ))}
                    </TextField>
                    <SearchField
                      className={classes.search}
                      value={searchValue}
                      label={lang("kanban.search")}
                      onChange={handleChangeSearch}
                    />
                    <Button
                      className={classes.addButton}
                      color="primary"
                      variant="contained"
                      size="small"
                      onClick={handleClickNew}
                    >
                      <AddIcon /> {lang("kanban.addContract")}
                    </Button>
                  </Grid>
                )}
                {currentTab === 1 && (
                  <Grid container item xs={12} sm md justifyContent="flex-end">
                    <ContractFilters />
                  </Grid>
                )}
              </PageHeader>
            )}
            {currentTab === 2 && (
              <PageHeader container spacing={1} padding={[0, 1.5]}>
                <Grid container item xs justifyContent="flex-start">
                  <BackButton onClick={handleClickClose} />
                  <PageTitle>{lang("kanban.addContract")}</PageTitle>
                </Grid>
              </PageHeader>
            )}
            <TabPanel value={currentTab} index={0}>
              <ContractsKanban />
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
              <ContractsDashboard />
            </TabPanel>
            <TabPanel value={currentTab} index={2}>
              <ContractForm
                open={openForm}
                onClose={handleClickClose}
                modal={false}
                editing={false}
              />
            </TabPanel>
          </Page>
        </>
      )}
      {enableLayerProfile && <Profile />}
      <SystemMessageSnack />
    </>
  );
});

export default Kanban;
